/**
 * Bundle 101 (URGENT) - Survey Ops landing page.
 *
 * Route: /admin/surveys (super_admin only)
 *
 * Lists every event that has (or ever had) a survey campaign along
 * with headline counts (invited / responded / submission rate). This
 * is the entry point the sidebar links to; the user drills into a
 * specific event's Respondents view from here.
 *
 * Data source: GET /api/admin/surveys/index (Bundle 101)
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertOctagon, Search, ExternalLink, Pause, Play,
          CalendarClock } from 'lucide-react';
import api from '@/utils/api';

const STATUS_BADGE = {
  active: 'bg-success-50 text-success-700 border-success-200',
  paused: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  draft: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  closed: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

const AdminSurveysLandingPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [summary, setSummary] = useState(null);
  const [pre89Summary, setPre89Summary] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [ix, sm, p89] = await Promise.all([
        api.get('/admin/surveys/index', {
          params: {
            q: q || undefined,
            status: statusFilter === 'all' ? undefined : statusFilter,
          },
        }),
        api.get('/admin/surveys/summary').catch(() => ({ data: null })),
        api.get('/admin/incident/pre-bundle89-recipients').catch(() => ({ data: null })),
      ]);
      setRows(ix.data?.rows || []);
      setSummary(sm?.data || null);
      setPre89Summary(p89?.data?.summary || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = useMemo(() => rows, [rows]);

  return (
    <div className="p-8 space-y-6 max-w-7xl" data-testid="admin-surveys-landing-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900">
            Survey Ops
          </h1>
          <p className="text-neutral-600 text-sm">
            Every event with a post-event survey campaign. Drill in to see
            who was invited, who responded, and manage the campaign schedule.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/incident/legacy-survey-recipients">
            <Button
              variant="outline"
              className="border-red-300 text-red-800 hover:bg-red-50"
              data-testid="landing-incident-recipients-btn"
            >
              <AlertOctagon className="h-4 w-4 mr-2" /> Incident recipients
            </Button>
          </Link>
        </div>
      </div>

      {pre89Summary && pre89Summary.total_mismatched > 0 ? (
        <Link to="/admin/incident/pre-bundle89-recipients"
              className="block"
              data-testid="pre89-attention-strip">
          <div className="border border-red-300 bg-red-50 text-red-900 rounded-lg p-3 hover:bg-red-100 transition-colors flex items-center gap-3">
            <AlertOctagon className="h-5 w-5 shrink-0 text-red-700" />
            <div className="text-sm flex-1">
              <span className="font-semibold">{pre89Summary.total_mismatched} attendees</span>
              {' '}received a pre-Bundle-89 survey.{' '}
              <span className="font-semibold">
                {pre89Summary.submitted_on_old_schema} of them submitted answers
              </span>{' '}on the old schema.
            </div>
            <span className="text-xs font-medium underline">Review and resend</span>
          </div>
        </Link>
      ) : null}

      {summary ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3"
              data-testid="survey-ops-summary">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-widest text-neutral-500">
                Submissions last 24h
              </div>
              <div className="text-2xl font-display font-bold text-primary-900 mt-1"
                    data-testid="summary-submissions-24h">
                {summary.submissions_last_24h ?? 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-widest text-neutral-500">
                Paused campaigns
              </div>
              <div className="text-2xl font-display font-bold text-primary-900 mt-1"
                    data-testid="summary-paused">
                {summary.paused_campaigns ?? 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-widest text-neutral-500">
                Pending apologies
              </div>
              <div className="text-2xl font-display font-bold text-primary-900 mt-1"
                    data-testid="summary-pending-apologies">
                {summary.pending_apologies ?? 0}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            className="pl-9"
            placeholder="Search by event title, city, or slug"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchAll(); }}
            data-testid="survey-ops-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); }}>
          <SelectTrigger className="w-40" data-testid="survey-ops-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={fetchAll}
          className="bg-primary-900 text-white hover:bg-primary-800"
          data-testid="survey-ops-refresh-btn"
        >Apply</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="survey-ops-table">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="p-3">Event</th>
                <th className="p-3">Date</th>
                <th className="p-3">Campaign</th>
                <th className="p-3 text-right">Invited</th>
                <th className="p-3 text-right">Started</th>
                <th className="p-3 text-right">Submitted</th>
                <th className="p-3 text-right">Rate</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-6 text-center text-neutral-500">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-6 text-center text-neutral-500"
                        data-testid="survey-ops-empty">
                    No events with survey campaigns yet.
                  </td>
                </tr>
              ) : filtered.map((r) => (
                <tr key={r.event_id}
                      className="border-t border-neutral-100 hover:bg-neutral-50"
                      data-testid={`survey-ops-row-${r.event_id}`}>
                  <td className="p-3">
                    <div className="font-medium text-primary-900">{r.title}</div>
                    <div className="text-xs text-neutral-500">{r.city || ''}</div>
                  </td>
                  <td className="p-3 text-neutral-700">
                    {r.start_datetime ? new Date(r.start_datetime).toLocaleDateString() : '-'}
                  </td>
                  <td className="p-3">
                    <Badge className={`${STATUS_BADGE[r.campaign_status] || STATUS_BADGE.draft} border`}>
                      {r.campaign_status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right font-mono text-neutral-800"
                        data-testid={`row-invited-${r.event_id}`}>{r.invited}</td>
                  <td className="p-3 text-right font-mono text-neutral-800">{r.started}</td>
                  <td className="p-3 text-right font-mono text-primary-900 font-semibold"
                        data-testid={`row-submitted-${r.event_id}`}>{r.submitted}</td>
                  <td className="p-3 text-right text-primary-800 font-semibold">
                    {r.submission_rate_pct}%
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <Link to={`/admin/events/${r.event_id}/survey/respondents`}>
                        <Button size="sm" variant="outline"
                                  data-testid={`row-respondents-btn-${r.event_id}`}>
                          Respondents
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Button>
                      </Link>
                      <Link to={`/admin/events/${r.event_id}/survey`}>
                        <Button size="sm" variant="outline"
                                  data-testid={`row-campaign-btn-${r.event_id}`}>
                          <CalendarClock className="h-3 w-3 mr-1" />
                          Campaign
                        </Button>
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSurveysLandingPage;
